import {
  Launch as DetailsIcon,
  DoNotDisturb,
  ChatBubble as ThreadIcon,
} from "@mui/icons-material";
import { Typography } from "@mui/material";
import { keyBy } from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useState } from "react";

import { GetAudienceContactFieldsQuery } from "~frontend/__generated__/graphql";
import Table, {
  RowAction,
  TableBox,
  TableColumnAlignment,
} from "~frontend/components/Table";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { useContactNameFormatter } from "~frontend/util/formatContactName";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";

import SubscriberDialog from "./SubscriberDialog";
import { Subscriber, subscriberIsOptedOut } from "./subscribersUtils";
import { useSubscriberLink } from "./subscribersUtils";
import { Shortcode } from "./types";

export default function SubscribersTable({
  shortcode,
  subscribers,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalCount,
  audienceContactFields,
}: {
  shortcode: Shortcode;
  subscribers: Subscriber[];
  page: number;
  setPage: (newPage: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (newRowsPerPage: number) => void;
  totalCount: number;
  audienceContactFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
}): ReactElement {
  const [dialogOpenFor, setDialogOpenFor] = useState<Subscriber | null>(null);
  const subscriberLink = useSubscriberLink();
  const formatSubscriberName = useContactNameFormatter();
  const customFieldsAllowed = useFeatureFlag("audienceCustomFields");

  return (
    <TableBox>
      <Table<Subscriber>
        rows={subscribers}
        columns={[
          {
            key: "phoneNumber",
            title: "Number",
            align: "left",
            renderValue: subscriber =>
              subscriberIsOptedOut(subscriber) ? (
                <>
                  {prettyPhoneNumber(subscriber.phoneNumber)}
                  <br />
                  <Typography
                    sx={{
                      fontStyle: "italic",
                      display: "flex",
                      alignItems: "center",
                      mt: 0.5,
                    }}
                    variant="body3"
                  >
                    <DoNotDisturb
                      sx={{ width: "10px", height: "10px", mr: 0.5 }}
                    />
                    Opted Out
                  </Typography>
                </>
              ) : (
                prettyPhoneNumber(subscriber.phoneNumber)
              ),
          },
          {
            key: "name",
            title: "Name",
            align: "left",
            renderValue: subscriber =>
              formatSubscriberName(
                {
                  firstName: subscriber.fields?.find(
                    f => f.name === "first_name"
                  )?.value,
                  lastName: subscriber.fields?.find(f => f.name === "last_name")
                    ?.value,
                },
                {
                  overrideNoNameText: "-",
                }
              ),
          },
          {
            key: "address",
            title: "Address",
            align: "left",
            renderValue: subscriber => {
              const fields = keyBy(subscriber.fields, "name") || {};
              return (
                <>
                  {fields.address_line_1?.value}
                  {fields.address_line_1?.value &&
                    fields.address_line_2?.value &&
                    ", "}
                  {fields.address_line_2?.value}
                  {(fields.address_line_1?.value ||
                    fields.address_line_2?.value) &&
                    (fields.city?.value ||
                      fields.state?.value ||
                      fields.zip?.value) && <br />}
                  {[fields.city?.value, fields.state?.value]
                    .filter(Boolean)
                    .join(", ")}{" "}
                  {fields.zip.value}
                </>
              );
            },
          },
          {
            key: "added",
            title: "Added",
            align: "left",
            renderValue: subscriber => (
              <>
                {formatShortDateTime(DateTime.fromMillis(subscriber.createdAt))}
                {subscriber.updatedAt > subscriber.createdAt && (
                  <Typography display="inline" variant="body3">
                    <br />
                    Updated{" "}
                    {formatShortDateTime(
                      DateTime.fromMillis(subscriber.updatedAt)
                    )}
                  </Typography>
                )}
              </>
            ),
          },
          ...(customFieldsAllowed
            ? [
                {
                  key: "customFields",
                  title: "Custom Fields",
                  align: "left" as TableColumnAlignment,
                  sx: { maxWidth: "450px" },
                  renderValue: (subscriber: Subscriber) => {
                    // only built-in fields have displayName
                    const customFields = (subscriber.fields || []).filter(
                      f => !f.displayName
                    );
                    if (customFields.length === 0) {
                      return <div />;
                    }
                    return customFields.map(field => (
                      <div key={field.name}>
                        {field.name}
                        {field.type === "BOOLEAN" ? "?" : ""}:{" "}
                        {typeof field.value === "boolean" &&
                        field.value !== undefined
                          ? field.value
                            ? "True"
                            : "False"
                          : field.value}
                      </div>
                    ));
                  },
                },
              ]
            : []),
        ]}
        rowActions={subscriber => {
          const actions: RowAction<Subscriber>[] = [];

          if (subscriber.threadId) {
            actions.push({
              id: "view",
              tooltipLabel: "View thread",
              icon: <ThreadIcon />,
              type: "link",
              href: subscriberLink(subscriber),
            });
          }

          actions.push({
            id: "details",
            tooltipLabel: "Details",
            icon: <DetailsIcon />,
            onClick: subscriber => setDialogOpenFor(subscriber),
          });

          return actions;
        }}
        page={page}
        onChangePage={setPage}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={setRowsPerPage}
        totalCount={totalCount}
        emptyState="No subscribers found"
      />
      {dialogOpenFor && (
        <SubscriberDialog
          subscriber={dialogOpenFor}
          updateSubscriber={setDialogOpenFor}
          shortcode={shortcode}
          audienceContactFields={audienceContactFields}
          onClose={() => setDialogOpenFor(null)}
        />
      )}
    </TableBox>
  );
}
